import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import {
  buildDsarExportPackage,
  type DsarExportPackage,
} from './dsarExportModel';
import type {
  BalanceLedgerEntry,
  CashoutRequest,
  Click,
  ConsentRecord,
  Ticket,
  Transaction,
  User,
  WelcomeBonusFunding,
} from '../dataProvider/types';

/**
 * The DSAR export package, assembled through the seam (`buildDsarExportPackage`), NOT computed
 * in the component. It reads the member's OWN data from the RAW underlying collections (so the
 * member's cashout `payoutDetails` is included, which the member-facing `cashouts` projection
 * deliberately drops for a different audience), filtered to the subject. READ-ONLY: it only
 * ever reads; nothing here writes.
 *
 * The EXCLUDED categories (audit entries about them, impersonation sessions viewing them) are
 * never fetched here: the internal operational records about a member are not their personal
 * data, so they never enter the package (D-dsar).
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
};

export const useDsarExport = (
  subjectUserId: number,
): { pkg: DsarExportPackage | null; isLoading: boolean } => {
  const dataProvider = useDataProvider();

  const { data, isLoading } = useQuery({
    queryKey: ['dsar-export', subjectUserId],
    queryFn: async () => {
      const user = (await dataProvider.getOne('users', { id: subjectUserId }))
        .data as unknown as User;
      const subjectFilter = { filter: { userId: subjectUserId } };
      const [
        transactions,
        cashouts,
        ledgerEntries,
        clicks,
        tickets,
        welcomeBonuses,
        consents,
      ] = await Promise.all([
        dataProvider.getList('transactions', { ...ALL, ...subjectFilter }),
        dataProvider.getList('cashoutRequests', { ...ALL, ...subjectFilter }),
        dataProvider.getList('balanceLedger', { ...ALL, ...subjectFilter }),
        dataProvider.getList('clicks', { ...ALL, ...subjectFilter }),
        dataProvider.getList('tickets', { ...ALL, ...subjectFilter }),
        dataProvider.getList('welcomeBonusFundings', {
          ...ALL,
          ...subjectFilter,
        }),
        dataProvider.getList('consents', { ...ALL, ...subjectFilter }),
      ]);
      return buildDsarExportPackage({
        user,
        transactions: transactions.data as unknown as Transaction[],
        cashouts: cashouts.data as unknown as CashoutRequest[],
        ledgerEntries: ledgerEntries.data as unknown as BalanceLedgerEntry[],
        clicks: clicks.data as unknown as Click[],
        tickets: tickets.data as unknown as Ticket[],
        welcomeBonuses: welcomeBonuses.data as unknown as WelcomeBonusFunding[],
        consents: consents.data as unknown as ConsentRecord[],
      });
    },
  });

  return { pkg: data ?? null, isLoading };
};
